import { gql } from '@apollo/client';
import { WorkspaceGraphQLField } from '@xometry/workspace';
import { QueryName, QueryNameKey } from 'components/Workspaces/collections';

/** GraphQL type behind each workspace query, read by the introspection query. */
export const listQueryTypeNames: Record<QueryNameKey, string> = {
  [QueryName.BA]: 'WorkspacesBillingAccount',
  [QueryName.II]: 'WorkspacesIncomingInvoice',
  [QueryName.OI]: 'WorkspacesOutcomingInvoice',
  [QueryName.LI]: 'WorkspacesLogisticInvoice',
  [QueryName.QC]: 'WorkspacesQualityControl',
  [QueryName.PZ]: 'WorkspacesPayout',
  [QueryName.PI]: 'WorkspacesPayment',
  [QueryName.RF]: 'WorkspacesRefund',
  [QueryName.PR]: 'WorkspacesProvider',
  [QueryName.RfqPartners]: 'WorkspacesRfqPartner',
  [QueryName.SA]: 'ShippingAddress',
  [QueryName.HsCodes]: 'WorkspacesHsCode',
  [QueryName.SH]: 'WorkspacesShipping',
  [QueryName.SU]: 'WorkspacesStorage',
  [QueryName.OrganizationBalances]: 'WorkspacesOrganizationBalance',
  [QueryName.Organization]: 'WorkspacesOrganization',
  [QueryName.D]: 'WorkspacesDeal',
  [QueryName.Person]: 'WorkspacesPerson',
  [QueryName.E]: 'WorkspacesEnquiry',
  [QueryName.F]: 'WorkspacesFrameworkBatch',
  [QueryName.J]: 'WorkspacesJob',
  [QueryName.BP]: 'WorkspacesBankPayment',
  [QueryName.BPDraft]: 'WorkspacesBankPaymentsDraft',
  [QueryName.JO]: 'WorkspacesJobOffer',
  [QueryName.CL]: 'WorkspacesClaim',
  [QueryName.PO]: 'WorkspacesProviderOrder',
  [QueryName.User]: 'WorkspacesUser',
  [QueryName.Tasks]: 'WorkspacesTask',
  [QueryName.DraftingDealParts]: 'WorkspacesDraftingDealPart',
  [QueryName.DraftingEnquiryParts]: 'WorkspacesDraftingEnquiryPart',
  [QueryName.Coupons]: 'WorkspacesCoupon',
  [QueryName.MessageSubscriptions]: 'WorkspacesSubscription',
  [QueryName.Messages]: 'WorkspacesMessage',
  [QueryName.Emails]: 'WorkspacesEmailMessage',
  [QueryName.RfqOffers]: 'WorkspacesRfqOffer',
  [QueryName.Rfqs]: 'WorkspacesRfq',
  [QueryName.ProductionScheduleItems]: 'WorkspacesProductionScheduleItem',
  [QueryName.DealDocuments]: 'WorkspacesDealDocument',
  [QueryName.Teamspaces]: 'WorkspacesTeamspace',
  [QueryName.SFC]: 'WorkspacesEnquiriesSfCase',
  [QueryName.IntacctEvents]: 'WorkspacesIntacctEvent',
};

export const attributesQuery = (query: QueryNameKey) => gql`
  query {
    __type(name: "${listQueryTypeNames[query]}") {
      name
      fields {
        name
        description
        type {
          ...typeFragment
        }
      }
    }
  }
  fragment typeFragment on __Type {
    name
    kind
    fields {
      ...fieldsFragment
    }
    ofType {
      name
      kind
      fields {
        ...fieldsFragment
      }
      ofType {
        name
        kind
        fields {
          ...fieldsFragment
        }
        ofType {
          name
          kind
          fields {
            ...fieldsFragment
          }
        }
      }
    }
  }
  fragment leafFieldFragment on __Field {
    name
    description
    type {
      name
      kind
      fields {
        name
        description
        type {
          name
          kind
          fields {
              name
              description
              type {
                name
                kind
              }
            }
        }
      }
      ofType {
        name
        kind
        fields {
          name
          description
          type {
            name
            kind
            fields {
              name
              description
              type {
                name
                kind
              }
            }
          }
        }
        ofType {
          name
          kind
          fields {
            name
            description
            type {
              name
              kind
              fields {
              name
              description
              type {
                name
                kind
              }
            }
            }
          }
          ofType {
            name
            kind
            fields {
              name
              description
              type {
                name
                kind
                fields {
                  name
                  description
                  type {
                    name
                    kind
                  }
                }
              }
            }
          }
        }
      }
    }
  }
  fragment fieldsFragment on __Field {
    name
    description
    type {
      name
      kind
      fields {
        ...leafFieldFragment
      }
      ofType {
        name
        kind
        fields {
          ...leafFieldFragment
        }
        ofType {
          name
          kind
          fields {
            ...leafFieldFragment
          }
          ofType {
            name
            kind
            fields {
              ...leafFieldFragment
            }
          }
        }
      }
    }
  }
`;

export type ObjectQueryResponse = Record<
  QueryNameKey,
  {
    objects: Array<{ id: number }>;
    total: number;
  }
>;

const queryObjectAttributes = (object: WorkspaceGraphQLField): string => {
  if (object.type.kind !== 'OBJECT') {
    return object.name;
  }

  return `
  ${object.name} {
    ${object.type.fields.map((f) => queryObjectAttributes(f as WorkspaceGraphQLField)).join('\n')}
  }
  `;
};

export const objectQuery = (query: QueryNameKey, fields: WorkspaceGraphQLField[]) =>
  gql`
    query ${query}(
        $timezoneOffset: Int!,
        $filterQuery: String!,
        $queryOptions: JSON,
        $sortParams: [WorkspacesInputsGenericSortingType!],
        $paginationParams: WorkspacesInputsGenericPaginationType,
        $presetId: ID,
      ) {
      ${query} (
        timezoneOffset: $timezoneOffset
        filterQuery: $filterQuery
        queryOptions: $queryOptions
        sortParams: $sortParams
        paginationParams: $paginationParams
        presetId: $presetId
      ) {
        objects {
          ${fields.length ? fields.map((f) => queryObjectAttributes(f)).join('\n') : 'id'}
        }
        total
      }
    }
  `;

export type ExportMutationReponse = Record<string, { jobId: string }>;

export const buildExportMutation = (query: QueryNameKey) => {
  const exportQuery = `${query}Export`;

  return {
    exportMutationName: exportQuery,
    exportMutation: gql`
      mutation ${exportQuery}(
        $filename: String!,
        $format: String!,
        $columns: [WorkspacesInputsExportColumnType!]!,
        $timezoneOffset: Int!,
        $filterQuery: String!,
        $queryOptions: JSON,
        $sortParams: [WorkspacesInputsGenericSortingType!],
        $paginationParams: WorkspacesInputsGenericPaginationType!,
        $selectedRecords: [Int!],
      ) {
        ${exportQuery} (
          input: {
            filename: $filename
            format: $format
            columns: $columns
            timezoneOffset: $timezoneOffset
            filterQuery: $filterQuery
            queryOptions: $queryOptions
            sortParams: $sortParams
            paginationParams: $paginationParams
            selectedRecords: $selectedRecords
          }
        ) {
          jobId
        }
      }
    `,
  };
};
